export type SharedAccountingType = 'REFUND' | 'UPGRADE';

export type SharedAccountingIntent = {
    type: SharedAccountingType;
    membershipId: string;
    session: number;
    amount: number;
    paymentDate: string;
    /**
     * 그룹 전액 환불 플래그. 서버가 같은 키의 재시도인지 판정할 때 쓰는 지문에 이 값이
     * 들어 있으므로, 키에서 빠지면 "같은 키 + 다른 지문" = 영구 409 교착이 된다.
     *
     * 원칙: **서버 지문 ⊆ 프론트 키.** 요청 body에 실제로 실려 지문에 들어가는 필드만
     * 넣는다. 값이 `true`일 때만 키 문자열에 반영되므로 `false`/미지정인 기존 사용처
     * (PT 환불·업그레이드, 원매출 환불, 그룹 부분 환불, v1 그룹 환불)의 키는 그대로다.
     */
    isFullRefund?: boolean;
};

export type StableSharedAccountingOperation = {
    intentKey: string;
    operationId: string;
    createdAt: number;
};

type AccountingOperationStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StoredAccountingOperationRegistry = {
    version: 1;
    entries: Record<string, { operationId: string; createdAt: number }>;
};

export const SHARED_ACCOUNTING_OPERATION_STORAGE_KEY =
    'rappo.shared-membership-accounting-operations.v1';
export const SHARED_ACCOUNTING_OPERATION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/;

const normalizePaymentDate = (value: string): string => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value || '').trim() : parsed.toISOString();
};

/**
 * 서버의 immutable accounting payload와 같은 필드로 재시도 의도를 식별한다.
 *
 * `isFullRefund`는 `true`일 때만 뒤에 덧붙인다. 항상 넣으면(`isFullRefund: false`)
 * 저장돼 있던 모든 in-flight 키의 문자열이 바뀌어 무효화되고, 그 상태의 재시도는
 * 새 키로 나가 이중 환불이 된다. 값이 다를 때만 다른 키가 나오면 충분하다.
 */
export const getSharedAccountingIntentKey = (intent: SharedAccountingIntent): string =>
    JSON.stringify({
        version: 1,
        type: intent.type,
        membershipId: String(intent.membershipId || '').trim(),
        session: Number(intent.session),
        amount: Number(intent.amount),
        paymentDate: normalizePaymentDate(intent.paymentDate),
        ...(intent.isFullRefund === true ? { isFullRefund: true } : {}),
    });

const defaultUuidFactory = (): string => {
    const randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
    if (randomUUID) return randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getDefaultStorage = (): AccountingOperationStorage | undefined => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.getItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
            return window.localStorage;
        }
    } catch {
        // Safari privacy mode 등에서 접근 자체가 throw할 수 있다.
    }
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.getItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
            return window.sessionStorage;
        }
    } catch {
        // 저장소를 쓸 수 없어도 현재 화면 내 operation ref로는 재시도할 수 있다.
    }
    return undefined;
};

const emptyRegistry = (): StoredAccountingOperationRegistry => ({ version: 1, entries: {} });

const readRegistry = (
    storage: AccountingOperationStorage | undefined,
    now: number,
): StoredAccountingOperationRegistry => {
    if (!storage) return emptyRegistry();
    try {
        const raw = storage.getItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
        if (!raw) return emptyRegistry();
        const parsed = JSON.parse(raw) as Partial<StoredAccountingOperationRegistry>;
        if (parsed?.version !== 1 || !parsed.entries || typeof parsed.entries !== 'object') {
            storage.removeItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
            return emptyRegistry();
        }

        const entries: StoredAccountingOperationRegistry['entries'] = {};
        Object.entries(parsed.entries).forEach(([intentKey, entry]) => {
            const operationId = String(entry?.operationId || '').trim();
            const createdAt = Number(entry?.createdAt);
            if (
                OPERATION_ID_PATTERN.test(operationId) &&
                Number.isFinite(createdAt) &&
                createdAt <= now + 60_000 &&
                now - createdAt <= SHARED_ACCOUNTING_OPERATION_TTL_MS
            ) {
                entries[intentKey] = { operationId, createdAt };
            }
        });
        return { version: 1, entries };
    } catch {
        try {
            storage.removeItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
        } catch {
            // 손상된 저장소를 지우지 못해도 호출자는 새 in-memory operation을 사용한다.
        }
        return emptyRegistry();
    }
};

const writeRegistry = (
    storage: AccountingOperationStorage | undefined,
    registry: StoredAccountingOperationRegistry,
) => {
    if (!storage) return;
    try {
        if (Object.keys(registry.entries).length === 0) {
            storage.removeItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY);
            return;
        }
        storage.setItem(SHARED_ACCOUNTING_OPERATION_STORAGE_KEY, JSON.stringify(registry));
    } catch {
        // quota/보안 정책 오류는 화면을 중단시키지 않는다. 현재 ref는 계속 유지된다.
    }
};

type StableOperationOptions = {
    uuidFactory?: () => string;
    storage?: AccountingOperationStorage;
    now?: number;
};

/**
 * 응답 유실/5xx/409 뒤 같은 의도를 다시 제출하면 기존 operationId를 돌려준다.
 * 입력 payload가 바뀐 경우에만 새 operationId를 만든다.
 */
export const getStableSharedAccountingOperation = (
    current: StableSharedAccountingOperation | null | undefined,
    intent: SharedAccountingIntent,
    options: StableOperationOptions = {},
): StableSharedAccountingOperation => {
    const intentKey = getSharedAccountingIntentKey(intent);
    const now = options.now ?? Date.now();
    const storage = options.storage ?? getDefaultStorage();
    const registry = readRegistry(storage, now);
    const stored = registry.entries[intentKey];
    if (stored) return { intentKey, ...stored };

    if (
        current?.intentKey === intentKey &&
        now - current.createdAt <= SHARED_ACCOUNTING_OPERATION_TTL_MS
    ) {
        registry.entries[intentKey] = {
            operationId: current.operationId,
            createdAt: current.createdAt,
        };
        writeRegistry(storage, registry);
        return current;
    }

    const operation = {
        intentKey,
        operationId: `admin-pc-accounting:${(options.uuidFactory ?? defaultUuidFactory)()}`.slice(
            0,
            128,
        ),
        createdAt: now,
    };
    registry.entries[intentKey] = {
        operationId: operation.operationId,
        createdAt: operation.createdAt,
    };
    writeRegistry(storage, registry);
    return operation;
};

/**
 * 그룹 MemberTicket 업그레이드는 아직 서버 operation journal이 없어 키를 만들지 않는다.
 *
 * 환불은 예외다. 그룹 부분 환불은 서버가 판매금액을 먼저 깎고 환불 결제 레코드 생성에
 * 실패할 수 있고, 그 상태에서 재시도하면 금액이 한 번 더 깎인다. 서버가 재시도와 정당한
 * 2번째 환불을 구분하려면 클라이언트가 같은 키를 다시 보내는 방법밖에 없으므로,
 * PT와 같은 operation 관용구를 그룹 환불에도 적용한다.
 */
export const getStableSharedAccountingOperationForSource = (
    source: 'pt' | 'group',
    current: StableSharedAccountingOperation | null | undefined,
    intent: SharedAccountingIntent,
    options: StableOperationOptions = {},
): StableSharedAccountingOperation | undefined =>
    source === 'group' && intent.type !== 'REFUND'
        ? undefined
        : getStableSharedAccountingOperation(current, intent, options);

/**
 * 서버가 같은 clientOperationId의 최초 결과를 **부수효과 없이** 되돌려준 응답인지 본다.
 *
 * 재생(replay)을 성공으로 보여주면 "환불 완료" 화면 뒤에서 실제로는 돈이 움직이지 않은
 * 상태가 되어, 관리자가 의도한 2번째 환불이 조용히 삼켜진다. 화면은 이 값을 보고
 * 성공 안내 대신 "이미 처리된 요청"을 알려야 한다.
 *
 * axios 응답(`{ status, data }`)과 파싱된 본문 자체를 모두 받는다. 백엔드가 아직
 * 필드를 싣지 않으면 항상 `false`이므로 기존 동작이 그대로 유지된다(엄격히 `true`만 인정).
 */
/**
 * 재생(replay) 응답 판별.
 *
 * 경로마다 표식이 실리는 위치가 다르다. 하나라도 놓치면 "이미 처리된 요청"이
 * 새 환불 성공으로 표시되어, 관리자가 돈이 두 번 나갔다고 오해한다.
 *   - 공유 회계   : accountingOperation.replayed
 *   - 그룹 환불   : 응답 최상위 replayed
 *   - 원매출 환불 : refundOperation.replayed   (POST /:membershipPaymentId/refunds)
 */
export const isReplayedAccountingResponse = (result: unknown): boolean =>
    [result, (result as { data?: unknown } | null | undefined)?.data].some((node) => {
        const candidate = node as
            | {
                  replayed?: unknown;
                  accountingOperation?: { replayed?: unknown };
                  refundOperation?: { replayed?: unknown };
              }
            | null
            | undefined;
        return (
            candidate?.replayed === true ||
            candidate?.accountingOperation?.replayed === true ||
            candidate?.refundOperation?.replayed === true
        );
    });

/** 재생 응답 안내. 성공 문구와 섞이지 않게 두 화면이 같은 문구를 쓴다. */
export const REPLAYED_ACCOUNTING_ALERT_TITLE = '이미 처리된 요청입니다';
export const REPLAYED_ACCOUNTING_ALERT_MESSAGE =
    '앞서 접수된 같은 요청이 이미 처리되어, 이번 요청으로는 추가 처리가 발생하지 않았습니다. 결제 이력에서 처리 결과를 확인한 뒤, 추가 환불이 필요하면 새로 진행해주세요.';

/** 성공 응답을 확인한 정확한 operation만 지운다. 실패/응답 유실 때는 절대 지우지 않는다. */
export const clearStableSharedAccountingOperation = (
    operation: StableSharedAccountingOperation | null | undefined,
    storage: AccountingOperationStorage | undefined = getDefaultStorage(),
    now: number = Date.now(),
): void => {
    if (!operation || !storage) return;
    const registry = readRegistry(storage, now);
    if (registry.entries[operation.intentKey]?.operationId !== operation.operationId) return;
    delete registry.entries[operation.intentKey];
    writeRegistry(storage, registry);
};
